import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogContent,
  MenuItem,
  TextField,
} from "@mui/material";
import {
  CheckCircleOutline,
  Close,
  LocalShippingOutlined,
  ReceiptLongOutlined,
  Refresh,
  VisibilityOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { QRCodeSVG } from "qrcode.react";
import { getEInvoicePayload } from "./eInvoicePayload";

const api = axios.create({
  baseURL: 'https://retailxapp.com',
  headers: { "Content-Type": "application/json" },
});

const emptyRecord = { status: "NOT_GENERATED" };
const emptyTransport = {
  distance: "",
  transporterGstin: "",
  transporterName: "",
  mode: "1",
  documentNo: "",
  documentDate: "",
  vehicleNo: "",
  vehicleType: "R",
};

const statusCopy = {
  NOT_GENERATED: ["Not generated", "bg-slate-100 text-slate-700"],
  PROCESSING: ["Processing", "bg-amber-100 text-amber-800"],
  GENERATED: ["IRN generated", "bg-emerald-100 text-emerald-800"],
  FAILED: ["Needs attention", "bg-red-100 text-red-700"],
  CANCELLED: ["Cancelled", "bg-slate-200 text-slate-700"],
};

const toIrpDate = (value) => {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
};

const providerErrorLines = (provider) => {
  if (!provider) return [];
  let data = provider.data || provider;
  if (typeof data === "string") {
    try { data = JSON.parse(data); } catch { return [data]; }
  }
  const details = data.Error || data.ErrorDetails || data.errorDetails || data.errors || data.error;
  if (Array.isArray(details)) {
    return details.map((item) =>
      [item.ErrorCode || item.code, item.ErrorMessage || item.message].filter(Boolean).join(" — "),
    ).filter(Boolean);
  }
  if (details && typeof details === "object") {
    return [[details.ErrorCode || details.code, details.ErrorMessage || details.message]
      .filter(Boolean).join(" — ") || JSON.stringify(details)];
  }
  return [data.message || data.Message].filter(Boolean);
};

const payloadLabels = {
  Version: "Schema version",
  TaxSch: "Tax scheme",
  SupTyp: "Supply type",
  Typ: "Document type",
  No: "Invoice number",
  Dt: "Invoice date",
  Gstin: "GSTIN",
  LglNm: "Legal name",
  TrdNm: "Trade name",
  Addr1: "Address",
  Loc: "City / location",
  Pin: "PIN code",
  Stcd: "State code",
  Ph: "Phone",
  Em: "Email",
  Pos: "Place of supply",
  SlNo: "Sr. no.",
  PrdDesc: "Product / service",
  IsServc: "Is service",
  HsnCd: "HSN code",
  Qty: "Quantity",
  Unit: "Unit",
  UnitPrice: "Unit price",
  TotAmt: "Gross amount",
  Discount: "Discount",
  AssAmt: "Taxable amount",
  GstRt: "GST rate",
  CgstAmt: "CGST",
  SgstAmt: "SGST",
  IgstAmt: "IGST",
  TotItemVal: "Item total",
  AssVal: "Taxable value",
  CgstVal: "CGST total",
  SgstVal: "SGST total",
  IgstVal: "IGST total",
  CesVal: "Cess",
  StCesVal: "State cess",
  OthChrg: "Other charges",
  RndOffAmt: "Round off",
  TotInvVal: "Invoice total",
};

const submittedPayload = (payload) => JSON.parse(JSON.stringify(payload));

const displayValue = (value) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (value === 0) return "0";
  return value || "—";
};

function PayloadFields({ data, columns = 2 }) {
  if (!data) return null;
  return (
    <div className={`grid gap-x-5 gap-y-3 ${columns === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
      {Object.entries(data).map(([key, value]) => (
        <div key={key} className="min-w-0 border-b border-slate-100 pb-2">
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-400">
            <span>{payloadLabels[key] || key}</span>
            <span className="normal-case tracking-normal text-slate-300">({key})</span>
          </div>
          <div className="mt-1 break-words text-sm font-semibold text-slate-800">{displayValue(value)}</div>
        </div>
      ))}
    </div>
  );
}

function InvoicePayloadPreview({ payload }) {
  if (!payload) return null;
  const itemKeys = Array.from(new Set((payload.ItemList || []).flatMap((item) => Object.keys(item))));

  return (
    <div data-testid="e-invoice-payload-preview" className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 bg-slate-900 px-5 py-4 text-white sm:px-6">
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-orange-300">
              <VisibilityOutlined sx={{ fontSize: 17 }} /> Read-only submission preview
            </div>
            <h3 className="mt-1 text-xl font-bold">Tax Invoice #{payload.DocDtls?.No}</h3>
            <p className="mt-1 text-xs text-slate-300">Every value below is included in the e-invoice request sent to IRP.</p>
          </div>
          <div className="rounded-xl border border-white/15 bg-white/10 px-3 py-2 text-left sm:text-right">
            <div className="text-[10px] font-bold uppercase tracking-wide text-slate-300">Invoice date</div>
            <div className="mt-0.5 font-bold">{payload.DocDtls?.Dt || "—"}</div>
          </div>
        </div>
      </div>

      <div className="space-y-6 p-5 sm:p-6">
        <section>
          <div className="mb-3 text-xs font-bold uppercase tracking-[0.12em] text-orange-600">Request & document</div>
          <PayloadFields
            columns={3}
            data={{
              Version: payload.Version,
              ...(payload.TranDtls || {}),
              ...(payload.DocDtls || {}),
            }}
          />
        </section>

        <div className="grid gap-6 lg:grid-cols-2">
          <section className="rounded-xl bg-slate-50 p-4">
            <div className="mb-3 text-xs font-bold uppercase tracking-[0.12em] text-slate-500">From · SellerDtls</div>
            <PayloadFields data={payload.SellerDtls} />
          </section>
          <section className="rounded-xl bg-slate-50 p-4">
            <div className="mb-3 text-xs font-bold uppercase tracking-[0.12em] text-slate-500">Bill to · BuyerDtls</div>
            <PayloadFields data={payload.BuyerDtls} />
          </section>
        </div>

        <section>
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="text-xs font-bold uppercase tracking-[0.12em] text-orange-600">Items · ItemList</div>
            <div className="text-xs font-semibold text-slate-400">{payload.ItemList?.length || 0} item(s)</div>
          </div>
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="min-w-max w-full border-collapse text-left text-xs">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  {itemKeys.map((key) => (
                    <th key={key} className="whitespace-nowrap border-b border-slate-200 px-3 py-2.5 font-bold">
                      <div>{payloadLabels[key] || key}</div>
                      <div className="mt-0.5 text-[9px] font-medium text-slate-300">{key}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(payload.ItemList || []).map((item, index) => (
                  <tr key={item.SlNo || index} className="border-b border-slate-100 last:border-0">
                    {itemKeys.map((key) => (
                      <td key={key} className="max-w-[220px] whitespace-nowrap px-3 py-3 font-medium text-slate-700">
                        <div className={key === "PrdDesc" ? "max-w-[220px] truncate" : ""} title={String(displayValue(item[key]))}>
                          {displayValue(item[key])}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="rounded-xl border border-orange-100 bg-orange-50/50 p-4">
          <div className="mb-3 text-xs font-bold uppercase tracking-[0.12em] text-orange-700">Invoice totals · ValDtls</div>
          <PayloadFields columns={3} data={payload.ValDtls} />
        </section>

        <details className="rounded-xl border border-slate-200 bg-slate-50">
          <summary className="cursor-pointer px-4 py-3 text-sm font-bold text-slate-700">View exact API data (JSON)</summary>
          <pre className="max-h-80 overflow-auto border-t border-slate-200 p-4 text-[11px] leading-5 text-slate-600">
            {JSON.stringify(payload, null, 2)}
          </pre>
        </details>
      </div>
    </div>
  );
}

function Step({ done, active, number, title, subtitle }) {
  return (
    <div className="relative z-10 flex min-w-0 items-center gap-3 bg-slate-50 pr-3 last:pr-0">
      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ring-4 ring-slate-50 ${
        done ? "bg-emerald-500 text-white" : active ? "bg-[#f97316] text-white" : "bg-slate-200 text-slate-500"
      }`}>
        {done ? <CheckCircleOutline sx={{ fontSize: 18 }} /> : number}
      </div>
      <div className="min-w-0">
        <div className={`truncate text-sm font-semibold ${active || done ? "text-slate-900" : "text-slate-500"}`}>{title}</div>
        <div className="truncate text-[11px] text-slate-400">{subtitle}</div>
      </div>
    </div>
  );
}

function EInvoicePrintDocument({ invoice, payload, record }) {
  const items = payload?.ItemList || [];
  const totals = payload?.ValDtls || {};
  const seller = payload?.SellerDtls || {};
  const buyer = payload?.BuyerDtls || {};

  return (
    <div className="e-invoice-print bg-white p-8 text-slate-900">
      <style>{`
        @page { size: A4; margin: 10mm; }
        .e-invoice-print { font-family: Arial, sans-serif; font-size: 11px; line-height: 1.35; color: #111827; }
        .e-invoice-print table { width: 100%; border-collapse: collapse; }
        .e-invoice-print th, .e-invoice-print td { border: 1px solid #94a3b8; padding: 6px; vertical-align: top; }
        .e-invoice-print th { background: #f1f5f9; font-weight: 700; }
        .e-invoice-print .no-border td { border: 0; padding: 0; }
        .e-invoice-print .muted { color: #475569; }
        .e-invoice-print .section-title { background: #e2e8f0; font-weight: 700; padding: 6px 8px; }
        .e-invoice-print .qr-box { width: 178px; text-align: center; }
        .e-invoice-print .break-word { overflow-wrap: anywhere; }
        @media print { .e-invoice-print { padding: 0; } }
      `}</style>

      <table className="no-border mb-3">
        <tbody><tr>
          <td>
            <div className="text-lg font-bold">Tax Invoice / e-Invoice</div>
            <div className="mt-1 font-semibold">Invoice No: {payload?.DocDtls?.No || invoice?.INVNO || "—"}</div>
            <div>Invoice Date: {payload?.DocDtls?.Dt || "—"}</div>
          </td>
          <td className="qr-box">
            {record?.signedQrCode ? <QRCodeSVG value={record.signedQrCode} size={132} level="M" includeMargin /> : <div className="border border-slate-400 p-4">Signed QR code not returned</div>}
            <div className="mt-1 text-[9px] font-semibold">IRP SIGNED QR CODE</div>
          </td>
        </tr></tbody>
      </table>

      <table className="mb-3"><tbody>
        <tr><td colSpan="2" className="section-title">e-Invoice acknowledgement details</td></tr>
        <tr><td className="w-1/2"><strong>IRN:</strong> <span className="break-word">{record?.irn || "—"}</span></td><td><strong>Acknowledgement No.:</strong> {record?.ackNo || "—"}</td></tr>
        <tr><td><strong>Acknowledgement Date:</strong> {record?.ackDate || "—"}</td><td><strong>Supply Type:</strong> {payload?.TranDtls?.SupTyp || "B2B"}</td></tr>
        {record?.ewayBillNo && <tr><td><strong>E-Way Bill No.:</strong> {record.ewayBillNo}</td><td><strong>Valid Until:</strong> {record.ewayBillValidTill || "—"}</td></tr>}
      </tbody></table>

      <table className="mb-3"><tbody>
        <tr><td className="section-title w-1/2">Seller details</td><td className="section-title">Buyer details</td></tr>
        <tr>
          <td><strong>{seller.LglNm || "—"}</strong><br />{seller.Addr1 || "—"}<br />{[seller.Loc, seller.Pin].filter(Boolean).join(" - ")}<br /><strong>GSTIN:</strong> {seller.Gstin || record?.sellerGstin || "—"}</td>
          <td><strong>{buyer.LglNm || "—"}</strong><br />{buyer.Addr1 || "—"}<br />{[buyer.Loc, buyer.Pin].filter(Boolean).join(" - ")}<br /><strong>GSTIN:</strong> {buyer.Gstin || record?.buyerGstin || "—"}<br /><strong>Place of Supply:</strong> {buyer.Pos || "—"}</td>
        </tr>
      </tbody></table>

      <table className="mb-3">
        <thead><tr><th>Sr.</th><th>Description</th><th>HSN</th><th>Qty</th><th>Rate</th><th>Taxable Value</th><th>GST</th><th>Total</th></tr></thead>
        <tbody>{items.map((item, index) => <tr key={item.SlNo || index}><td>{item.SlNo || index + 1}</td><td>{item.PrdDesc}</td><td>{item.HsnCd}</td><td>{item.Qty} {item.Unit}</td><td>{item.UnitPrice}</td><td>{item.AssAmt}</td><td>{item.IgstAmt ? `IGST ${item.IgstAmt}` : `CGST ${item.CgstAmt || 0} + SGST ${item.SgstAmt || 0}`}</td><td>{item.TotItemVal}</td></tr>)}</tbody>
      </table>

      <table className="mb-3"><tbody>
        <tr><td className="w-2/3"><strong>Taxable Value:</strong> {totals.AssVal || 0}</td><td><strong>Invoice Value:</strong> {totals.TotInvVal || 0}</td></tr>
        <tr><td><strong>CGST:</strong> {totals.CgstVal || 0} &nbsp; <strong>SGST:</strong> {totals.SgstVal || 0} &nbsp; <strong>IGST:</strong> {totals.IgstVal || 0}</td><td><strong>Other Charges:</strong> {totals.OthChrg || 0}</td></tr>
      </tbody></table>

      <div className="mt-5 border-t border-slate-400 pt-2 text-center text-[9px] muted">This is a system-generated e-Invoice. The IRN and signed QR code above are generated through the Invoice Registration Portal.</div>
    </div>
  );
}

export default function EInvoiceFlowModal({ open, onClose, invoice, tablePrefix, companyDetails, token }) {
  const [record, setRecord] = useState(emptyRecord);
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [providerError, setProviderError] = useState(null);
  const [generatedLocally, setGeneratedLocally] = useState(false);
  const [payloadPreview, setPayloadPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [transport, setTransport] = useState(emptyTransport);
  const [cancel, setCancel] = useState({ reason: "2", remarks: "" });
  const printRef = useRef(null);
  const status = record?.status || "NOT_GENERATED";
  const [statusLabel, statusClass] = statusCopy[status] || statusCopy.NOT_GENERATED;

  const request = useCallback(async (path, extra = {}) => {
    if (!invoice?.INVNO) return null;
    setLoading(true);
    setError("");
    setProviderError(null);
    try {
      const response = await api.post(
        `/api/einvoice/${path}`,
        { tablePrefix, invoiceNo: String(invoice.INVNO), ...extra },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRecord(response.data.record || emptyRecord);
      if (response.data.eligibility) setEligibility(response.data.eligibility);
      return response.data.record;
    } catch (requestError) {
      setProviderError(requestError.response?.data?.provider || null);
      setError(
        requestError.response?.data?.message ||
          (requestError.code === "ERR_NETWORK"
            ? "Cannot reach the e-invoice backend. Check that it is running and the URL is configured."
            : "The e-invoice request could not be completed."),
      );
      return null;
    } finally {
      setLoading(false);
    }
  }, [invoice, tablePrefix, token]);

  const loadPayloadPreview = useCallback(async () => {
    if (!invoice?.INVNO) return null;
    setPreviewLoading(true);
    setPreviewError("");
    try {
      const payload = submittedPayload(await getEInvoicePayload({
        invoiceNo: String(invoice.INVNO),
        tablePrefix,
        companyDetails,
        token,
      }));
      setPayloadPreview(payload);
      return payload;
    } catch (payloadError) {
      setPayloadPreview(null);
      setPreviewError(payloadError.message || "Invoice preview could not be prepared.");
      return null;
    } finally {
      setPreviewLoading(false);
    }
  }, [companyDetails, invoice, tablePrefix, token]);

  useEffect(() => {
    if (open && invoice?.INVNO) {
      setRecord(emptyRecord);
      setEligibility(null);
      setProviderError(null);
      setGeneratedLocally(false);
      setPayloadPreview(null);
      setPreviewError("");
      setTransport(emptyTransport);
      setCancel({ reason: "2", remarks: "" });
      request("status");
      loadPayloadPreview();
    }
  }, [open, invoice?.INVNO, loadPayloadPreview, request]);

  const print = () => {
    if (!printRef.current) return;

    const printWindow = window.open("", "_blank", "width=900,height=1000");
    if (!printWindow) {
      setError("Print window could not open. Please allow pop-ups for this site and try again.");
      return;
    }

    printWindow.document.open();
    printWindow.document.write(`<!doctype html>
      <html><head><title>E-Invoice-${invoice?.INVNO || ""}</title>
      <meta charset="utf-8" />
      <style>
        @page { size: A4; margin: 10mm; }
        * { box-sizing: border-box; }
        body { margin: 0; background: #fff; }
        .e-invoice-print { font-family: Arial, sans-serif; font-size: 11px; line-height: 1.35; color: #111827; padding: 0; }
        .e-invoice-print table { width: 100%; border-collapse: collapse; }
        .e-invoice-print th, .e-invoice-print td { border: 1px solid #94a3b8; padding: 6px; vertical-align: top; }
        .e-invoice-print th { background: #f1f5f9; font-weight: 700; }
        .e-invoice-print .no-border td { border: 0; padding: 0; }
        .e-invoice-print .muted { color: #475569; }
        .e-invoice-print .section-title { background: #e2e8f0; font-weight: 700; padding: 6px 8px; }
        .e-invoice-print .qr-box { width: 178px; text-align: center; }
        .e-invoice-print .break-word { overflow-wrap: anywhere; }
      </style></head><body>${printRef.current.innerHTML}</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    window.setTimeout(() => printWindow.print(), 300);
  };

  const generate = async () => {
    setLoading(true);
    setError("");
    setProviderError(null);
    try {
      const payload = payloadPreview || await loadPayloadPreview();
      if (!payload) return;

      console.log("[E-INVOICE] Payload created from RetailX APIs", payload);
      const response = await api.post(
        "/api/einvoice/payload/generate",
        {
          payload,
          tablePrefix,
          invoiceNo: String(invoice.INVNO),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const result = response.data?.data || {};
      const savedRecord = response.data?.record || {};

      setEligibility({ eligible: true, invoiceType: "B2B", partySource: "ACCOUNT_MASTER" });
      setGeneratedLocally(false);
      setRecord({
        ...savedRecord,
        status: "GENERATED",
        invoiceNo: savedRecord.invoiceNo || String(invoice.INVNO),
        irn: result.irn || savedRecord.irn,
        ackNo: result.acknowledgementNumber || savedRecord.ackNo,
        ackDate: result.acknowledgementDate || savedRecord.ackDate,
        signedQrCode: result.signedQrCode || savedRecord.signedQrCode,
        ewayBillNo: result.ewayBillNumber || savedRecord.ewayBillNo,
        ewayBillDate: result.ewayBillDate || savedRecord.ewayBillDate,
        ewayBillValidTill: result.ewayBillValidTill || savedRecord.ewayBillValidTill,
        sellerGstin: savedRecord.sellerGstin || payload.SellerDtls.Gstin,
        buyerGstin: savedRecord.buyerGstin || payload.BuyerDtls.Gstin,
        environment: savedRecord.environment || "sandbox",
      });
    } catch (generateError) {
      setProviderError(generateError.response?.data?.provider || null);
      setError(
        generateError.response?.data?.message ||
          (generateError.code === "ERR_NETWORK"
            ? "Cannot reach invoice-backend on port 4000. Start it with npm run dev."
            : generateError.message || "E-invoice could not be generated."),
      );
    } finally {
      setLoading(false);
    }
  };
  const createEwayBill = () => request("ewaybill", {
    transport: { ...transport, documentDate: toIrpDate(transport.documentDate) },
  });
  const cancelIrn = () => request("cancel", cancel);
  const cancelEwayBill = () => request("ewaybill/cancel", cancel);
  const isGenerated = status === "GENERATED";
  const isCancelled = status === "CANCELLED";
  const isNotEligible = eligibility?.eligible === false;
  const canGenerateEwayBill = transport.distance !== "" && (
    transport.mode === "1"
      ? Boolean(transport.vehicleNo.trim() && transport.vehicleType)
      : Boolean(transport.documentNo.trim() && transport.documentDate)
  );
  const irnCancellationAllowed = record?.cancellation?.irn?.allowed !== false;
  const ewayCancellationAllowed = record?.cancellation?.ewayBill?.allowed !== false;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="lg"
      scroll="paper"
      aria-labelledby="e-invoice-dialog-title"
      PaperProps={{
        sx: {
          width: "min(1040px, calc(100% - 32px))",
          maxHeight: "min(760px, calc(100% - 32px))",
          m: 2,
          borderRadius: { xs: 2.5, sm: 3 },
          boxShadow: "0 24px 70px rgba(15, 23, 42, 0.24)",
          overflow: "hidden",
        },
      }}
    >
      <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-100 px-5 py-4 sm:px-6">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange-50 text-[#ea580c]">
            <ReceiptLongOutlined />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 id="e-invoice-dialog-title" className="text-lg font-bold tracking-tight text-slate-900 sm:text-xl">
                E-Invoice <span className="font-medium text-slate-400">#{invoice?.INVNO}</span>
              </h2>
              <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${statusClass}`}>{statusLabel}</span>
              {record?.environment && (
                <span className="rounded-full border border-slate-200 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  {record.environment}
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-slate-500">Validate the saved bill, generate its IRN and manage transport details.</p>
          </div>
        </div>
        <button type="button" aria-label="Close e-invoice dialog" onClick={onClose} disabled={loading} className="-mr-2 rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-200 disabled:cursor-not-allowed disabled:opacity-50">
          <Close fontSize="small" />
        </button>
      </div>

      <DialogContent sx={{ p: 0 }}>
        <div className="border-b border-slate-100 bg-slate-50 px-5 py-4 sm:px-6">
          <div className="relative grid gap-3 sm:grid-cols-3 sm:gap-6 before:absolute before:left-4 before:right-4 before:top-4 before:hidden before:h-px before:bg-slate-200 sm:before:block">
            <Step number="1" title="Validate bill" subtitle="RetailX source data" done={isGenerated || isCancelled} active={!isGenerated && !isCancelled} />
            <Step number="2" title="Generate IRN" subtitle="Signed invoice & QR" done={isGenerated || isCancelled} active={status === "PROCESSING"} />
            <Step number="3" title="Transport" subtitle="Optional e-way bill" done={Boolean(record?.ewayBillNo)} active={isGenerated && !record?.ewayBillNo} />
          </div>
        </div>

        <div className="space-y-4 p-5 sm:p-6">
          {error && (
            <Alert severity="error" icon={<WarningAmberOutlined />} sx={{ borderRadius: 2, py: 0.5 }}>
              <div className="font-semibold">{error}</div>
              {providerErrorLines(providerError).length > 0 && (
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
                  {providerErrorLines(providerError).map((line, index) => <li key={`${line}-${index}`}>{line}</li>)}
                </ul>
              )}
              {providerError && (
                <details className="mt-3">
                  <summary className="cursor-pointer text-xs font-semibold">View provider response</summary>
                  <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap rounded-lg bg-red-950/5 p-3 text-xs">
                    {JSON.stringify(providerError, null, 2)}
                  </pre>
                </details>
              )}
            </Alert>
          )}
          {record?.errorMessage && !error && <Alert severity="warning">{record.errorMessage}</Alert>}
          {isNotEligible && !error && (
            <Alert severity="info">
              <strong>{eligibility.invoiceType || "B2C"} invoice:</strong> {eligibility.reason}
            </Alert>
          )}
          {eligibility?.eligible && eligibility?.partySource === "ACCOUNT_MASTER" && !error && (
            <Alert severity="success" sx={{ borderRadius: 2, py: 0.25 }}>
              Buyer details checked. Missing values were retrieved securely from Account Master.
            </Alert>
          )}

          {previewLoading && (
            <div className="flex min-h-[180px] items-center justify-center rounded-2xl border border-slate-200 bg-slate-50">
              <div className="text-center">
                <CircularProgress size={24} sx={{ color: "#f97316" }} />
                <div className="mt-3 text-sm font-semibold text-slate-600">Preparing the exact invoice data preview…</div>
              </div>
            </div>
          )}
          {previewError && !previewLoading && (
            <Alert
              severity="warning"
              action={
                <button type="button" onClick={loadPayloadPreview} className="rounded-lg px-3 py-1.5 text-xs font-bold text-amber-800 hover:bg-amber-100">
                  Retry preview
                </button>
              }
              sx={{ borderRadius: 2 }}
            >
              <div className="font-semibold">Invoice preview is not ready</div>
              <div className="text-sm">{previewError}</div>
            </Alert>
          )}
          {!previewLoading && payloadPreview && <InvoicePayloadPreview payload={payloadPreview} />}

          {!isGenerated && !isCancelled && (
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="flex flex-col gap-5 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
                <div className="min-w-0">
                  <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-orange-600">
                    <CheckCircleOutline sx={{ fontSize: 17 }} /> Ready for submission
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">{isNotEligible ? "E-invoice not applicable" : "Validate and generate IRN"}</h3>
                  <p className="mt-1 max-w-lg text-sm leading-6 text-slate-500">
                    {isNotEligible
                      ? "Add a valid GSTIN to the customer and save a corrected B2B invoice if this sale should be reported to IRP."
                      : "We’ll verify GSTIN, HSN, tax totals, address and PIN details before securely submitting this bill."}
                  </p>
                </div>
                <button type="button" onClick={generate} disabled={loading || previewLoading || status === "PROCESSING" || isNotEligible} className="inline-flex min-h-[46px] w-full shrink-0 items-center justify-center gap-2 rounded-xl bg-[#f97316] px-5 py-3 text-sm font-bold text-white shadow-sm transition-colors hover:bg-[#ea580c] focus:outline-none focus:ring-2 focus:ring-orange-300 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500 disabled:opacity-100 sm:w-auto sm:min-w-[160px]">
                  {loading ? <CircularProgress size={18} color="inherit" /> : <ReceiptLongOutlined fontSize="small" />}
                  {isNotEligible ? "Not eligible" : "Generate IRN"}
                </button>
              </div>
            </div>
          )}

          {(isGenerated || isCancelled) && (
            <div className="overflow-hidden rounded-2xl border border-emerald-200 bg-emerald-50/40">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-emerald-200 bg-emerald-100/50 px-5 py-3">
                <div>
                  <h3 className="font-bold text-emerald-900">e-Invoice acknowledgement</h3>
                  <p className="text-sm text-emerald-700">Print-ready tax invoice with IRN and signed QR code.</p>
                </div>
                <button onClick={print} className="rounded-lg border border-emerald-300 bg-white px-3 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-50">Print e-Invoice</button>
              </div>
              <div ref={printRef}>
                <EInvoicePrintDocument invoice={invoice} payload={payloadPreview} record={record} />
              </div>
            </div>
          )}

          {isGenerated && !generatedLocally && !record?.ewayBillNo && (
            <div className="rounded-2xl border border-slate-200 p-5">
              <div className="mb-4 flex items-center gap-3">
                <div className="rounded-xl bg-blue-50 p-2.5 text-blue-600"><LocalShippingOutlined /></div>
                <div>
                  <h3 className="font-bold text-slate-900">Create e-way bill</h3>
                  <p className="text-sm text-slate-500">Optional. Add transport details after the IRN is generated.</p>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <TextField size="small" required label="Distance (km)" type="number" value={transport.distance} onChange={(e) => setTransport({ ...transport, distance: e.target.value })} />
                <TextField select size="small" label="Transport mode" value={transport.mode} onChange={(e) => setTransport({ ...transport, mode: e.target.value })}>
                  <MenuItem value="1">Road</MenuItem><MenuItem value="2">Rail</MenuItem><MenuItem value="3">Air</MenuItem><MenuItem value="4">Ship</MenuItem>
                </TextField>
                <TextField size="small" label="Transporter GSTIN" value={transport.transporterGstin} onChange={(e) => setTransport({ ...transport, transporterGstin: e.target.value.toUpperCase() })} />
                <TextField size="small" label="Transporter name" value={transport.transporterName} onChange={(e) => setTransport({ ...transport, transporterName: e.target.value })} />
                <TextField size="small" label="Document number" value={transport.documentNo} onChange={(e) => setTransport({ ...transport, documentNo: e.target.value })} />
                <TextField size="small" label="Document date" type="date" InputLabelProps={{ shrink: true }} value={transport.documentDate} onChange={(e) => setTransport({ ...transport, documentDate: e.target.value })} />
                <TextField size="small" label="Vehicle number" value={transport.vehicleNo} onChange={(e) => setTransport({ ...transport, vehicleNo: e.target.value.toUpperCase() })} />
                <TextField select size="small" label="Vehicle type" value={transport.vehicleType} onChange={(e) => setTransport({ ...transport, vehicleType: e.target.value })}>
                  <MenuItem value="R">Regular</MenuItem><MenuItem value="O">Over-dimensional cargo</MenuItem>
                </TextField>
              </div>
              <button onClick={createEwayBill} disabled={loading || !canGenerateEwayBill} className="mt-4 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-50">
                {loading ? <CircularProgress size={18} color="inherit" /> : <LocalShippingOutlined fontSize="small" />} Generate e-way bill
              </button>
              {!canGenerateEwayBill && transport.distance !== "" && (
                <p className="mt-2 text-xs font-medium text-amber-700">
                  {transport.mode === "1"
                    ? "Vehicle number is required for road transport."
                    : "Transport document number and date are required for rail, air or ship."}
                </p>
              )}
            </div>
          )}

          {record?.ewayBillNo && !generatedLocally && (
            <div className="rounded-2xl border border-blue-200 bg-blue-50 p-5">
              <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wide text-blue-600">Active e-way bill</div>
                  <div className="mt-1 text-xl font-bold text-blue-950">{record.ewayBillNo}</div>
                  <div className="mt-1 text-sm text-blue-700">Generated {record.ewayBillDate || "—"} · Valid until {record.ewayBillValidTill || "—"}</div>
                </div>
                <button onClick={cancelEwayBill} disabled={loading || !ewayCancellationAllowed || cancel.remarks.length > 100} className="rounded-xl border border-red-200 bg-white px-4 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40">Cancel e-way bill</button>
              </div>
              {!ewayCancellationAllowed && (
                <p className="mt-3 text-sm font-semibold text-red-700">The 24-hour e-way bill cancellation period has expired.</p>
              )}
            </div>
          )}

          {isGenerated && !generatedLocally && (
            <div className="rounded-2xl border border-red-100 bg-red-50/50 p-5">
              <h3 className="font-bold text-slate-900">Cancellation</h3>
              <p className="mt-1 text-sm text-slate-500">
                {irnCancellationAllowed
                  ? "IRN cancellation is available within 24 hours. Cancel the e-way bill first."
                  : "The 24-hour IRN cancellation period has expired."}
              </p>
              <div className="mt-4 grid gap-4 sm:grid-cols-[200px_1fr]">
                <TextField select size="small" label="Reason" value={cancel.reason} onChange={(e) => setCancel({ ...cancel, reason: e.target.value })}>
                  <MenuItem value="1">Duplicate</MenuItem><MenuItem value="2">Data entry mistake</MenuItem><MenuItem value="3">Order cancelled</MenuItem><MenuItem value="4">Other</MenuItem>
                </TextField>
                <TextField size="small" label="Remarks" value={cancel.remarks} onChange={(e) => setCancel({ ...cancel, remarks: e.target.value })} helperText={`${cancel.remarks.length}/100`} />
              </div>
              <button onClick={cancelIrn} disabled={loading || !irnCancellationAllowed || Boolean(record.ewayBillNo) || cancel.remarks.trim().length < 5} className="mt-4 rounded-xl border border-red-200 bg-white px-4 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-40">Cancel IRN</button>
            </div>
          )}
        </div>
      </DialogContent>

      <div className="flex shrink-0 items-center justify-between border-t border-slate-100 bg-white px-5 py-3.5 sm:px-6">
        {generatedLocally ? <span /> : (
          <button type="button" onClick={() => request("status")} disabled={loading} className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg px-2 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200 disabled:opacity-50">
            <Refresh fontSize="small" /> Refresh status
          </button>
        )}
        <button type="button" onClick={onClose} disabled={loading} className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-200 disabled:opacity-50">Close</button>
      </div>
    </Dialog>
  );
}
